import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, setToken } from '../api';
import { useI18n } from '../i18n/context';
import '../pages/Login.css';

export default function Login() {
  const navigate = useNavigate();
  const { t, locale, setLocale } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials');
  const [tempToken, setTempToken] = useState(null);
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Defensive cleanup for orphaned overlays/backdrops left by interrupted modal flows.
    const staleOverlays = document.querySelectorAll(
      '.ui-modal-backdrop, .sa-modal-overlay, .rd-backdrop'
    );
    staleOverlays.forEach((el) => el.remove());

    document.body.classList.remove('modal-open', 'sa-modal-open');
    document.body.style.removeProperty('overflow');
  }, []);

  async function handleSubmitCredentials(e) {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const data = await api('/superadmin/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password })
      });
      if (data.requires2fa && data.tempToken) {
        setTempToken(data.tempToken);
        setStep('2fa');
        setCode('');
      } else {
        if (data.token) setToken(data.token);
        navigate('/', { replace: true });
      }
    } catch (err) {
      if (err?.status === 429) {
        setError(err?.data?.error || t('login.errorRateLimit'));
      } else {
        setError(err.message || t('login.errorCredentials'));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit2FA(e) {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const data = await api('/superadmin/auth/verify-2fa', {
        method: 'POST',
        body: JSON.stringify({ tempToken, code: code.trim() })
      });
      if (data.token) setToken(data.token);
      navigate('/', { replace: true });
    } catch (err) {
      if (err?.status === 429) {
        setError(err?.data?.error || t('login.errorRateLimit'));
      } else {
        setError(err.message || t('login.errorCredentials'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sa-login-page">
      <div className="sa-login-locale">
        <button type="button" className={locale === 'en' ? 'sa-locale-btn sa-locale-btn--active' : 'sa-locale-btn'} onClick={() => setLocale('en')}>EN</button>
        <button type="button" className={locale === 'es' ? 'sa-locale-btn sa-locale-btn--active' : 'sa-locale-btn'} onClick={() => setLocale('es')}>ES</button>
      </div>
      <div className="sa-login-card">
        <div className="sa-login-logo">
          <img src="/logo.png" alt={t('login.logoAlt')} />
        </div>
        <h1 className="sa-login-title">{t('login.title')}</h1>
        <p className="sa-login-subtitle">{t('login.subtitle')}</p>

        {step === 'credentials' && (
          <form onSubmit={handleSubmitCredentials} className="sa-login-form">
            {error && <div className="sa-login-error">{error}</div>}
            <label className="sa-login-label">
              {t('login.email')}
              <input
                type="email"
                className="sa-input sa-login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
              />
            </label>
            <label className="sa-login-label">
              {t('login.password')}
              <div className="sa-login-password">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="sa-input sa-login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="sa-login-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPassword ? t('login.hide') : t('login.show')}
                </button>
              </div>
            </label>
            <button type="submit" className="sa-btn-primary sa-login-btn" disabled={loading}>
              <span className={loading ? 'sa-login-btn-content sa-login-btn-content--loading' : 'sa-login-btn-content'}>
                {loading && <span className="sa-login-spinner" aria-hidden="true" />}
                {loading ? t('login.submitting') : t('login.submit')}
              </span>
            </button>
          </form>
        )}

        {step === '2fa' && (
          <form onSubmit={handleSubmit2FA} className="sa-login-form">
            {error && <div className="sa-login-error">{error}</div>}
            <p className="sa-login-text">{t('login.twoFactorPrompt')}</p>
            <label className="sa-login-label">
              {t('login.twoFactorCode')}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="sa-input sa-login-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="000000"
                maxLength={8}
              />
            </label>
            <button type="submit" className="sa-btn-primary sa-login-btn" disabled={loading || code.length < 6}>
              <span className={loading ? 'sa-login-btn-content sa-login-btn-content--loading' : 'sa-login-btn-content'}>
                {loading && <span className="sa-login-spinner" aria-hidden="true" />}
                {loading ? t('login.submitting') : t('login.verify')}
              </span>
            </button>
            <button
              type="button"
              className="sa-btn-link sa-login-back"
              onClick={() => { setStep('credentials'); setError(''); setTempToken(null); }}
            >
              {t('login.back')}
            </button>
          </form>
        )}

        <div className="sa-login-security">
          <span className="sa-login-security-icon" aria-hidden="true">🔒</span>
          <div className="sa-login-security-text">
            <span>{t('login.secureAccess')}</span>
            <span>{t('login.footer')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
